import { env } from '@/shared/config/env'

const SESSION_ACTIVE_KEY = 'auth_session_active'
const PERSIST_KEY = 'auth_persist'

export interface AuthState {
  accessToken: string | null
  expiresAt: number | null
}

type Listener = (state: AuthState) => void

const state: AuthState = {
  accessToken: null,
  expiresAt: null,
}

const listeners = new Set<Listener>()

let proactiveRefresh: (() => void) | null = null
let refreshTimer: ReturnType<typeof setTimeout> | null = null

const PROACTIVE_LEAD_MS = 60_000

function emit() {
  const snapshot: AuthState = { ...state }
  listeners.forEach(l => {
    try {
      l(snapshot)
    } catch (err) {
      console.error('[auth-store] listener threw', err)
    }
  })
}

function scheduleProactiveRefresh() {
  if (refreshTimer) {
    clearTimeout(refreshTimer)
    refreshTimer = null
  }
  if (!state.expiresAt || !proactiveRefresh) return
  if (!env.authRefreshEnabled) return

  const delay = state.expiresAt - Date.now() - PROACTIVE_LEAD_MS
  const safeDelay = Math.max(0, delay)
  refreshTimer = setTimeout(() => {
    refreshTimer = null
    proactiveRefresh?.()
  }, safeDelay)
}

function safeStorage(driver: Storage | undefined): Storage | null {
  try {
    if (!driver) return null
    const key = '__as__'
    driver.setItem(key, '1')
    driver.removeItem(key)
    return driver
  } catch {
    return null
  }
}

const localDriver =
  typeof window !== 'undefined' ? safeStorage(window.localStorage) : null

const sessionDriver =
  typeof window !== 'undefined' ? safeStorage(window.sessionStorage) : null

export const authStore = {
  getState(): Readonly<AuthState> {
    return state
  },

  getAccessToken(): string | null {
    return state.accessToken
  },

  isAuthenticated(): boolean {
    if (!state.accessToken || !state.expiresAt) return false
    return state.expiresAt > Date.now()
  },

  setAccessToken(accessToken: string, expiresInSec: number): void {
    state.accessToken = accessToken
    state.expiresAt = Date.now() + Math.max(0, expiresInSec) * 1000
    scheduleProactiveRefresh()
    emit()
  },

  clear(): void {
    state.accessToken = null
    state.expiresAt = null
    if (refreshTimer) {
      clearTimeout(refreshTimer)
      refreshTimer = null
    }
    emit()
  },

  setSessionActive(persist: boolean): void {
    localDriver?.setItem(PERSIST_KEY, persist ? '1' : '0')
    if (persist) {
      localDriver?.setItem(SESSION_ACTIVE_KEY, '1')
      sessionDriver?.removeItem(SESSION_ACTIVE_KEY)
    } else {
      sessionDriver?.setItem(SESSION_ACTIVE_KEY, '1')
      localDriver?.removeItem(SESSION_ACTIVE_KEY)
    }
  },

  hasSessionMarker(): boolean {
    return (
      localDriver?.getItem(SESSION_ACTIVE_KEY) === '1' ||
      sessionDriver?.getItem(SESSION_ACTIVE_KEY) === '1'
    )
  },

  isPersistent(): boolean {
    return localDriver?.getItem(PERSIST_KEY) === '1'
  },

  getPersistPreference(): string | null {
    return localDriver?.getItem(PERSIST_KEY) ?? null
  },

  activateBrowserSession(): void {
    sessionDriver?.setItem(SESSION_ACTIVE_KEY, '1')
  },

  clearSessionMarkers(): void {
    localDriver?.removeItem(SESSION_ACTIVE_KEY)
    localDriver?.removeItem(PERSIST_KEY)
    sessionDriver?.removeItem(SESSION_ACTIVE_KEY)
  },

  subscribe(listener: Listener): () => void {
    listeners.add(listener)
    try {
      listener({ ...state })
    } catch (err) {
      console.error('[auth-store] listener threw on subscribe', err)
    }
    return () => {
      listeners.delete(listener)
    }
  },

  __registerProactiveRefresh(cb: () => void): void {
    proactiveRefresh = cb
  },
}
